import { useState, useMemo, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Edit2, Trash2, Plus, RefreshCw, AlertTriangle } from 'lucide-react';
import { apiClient } from '@/services/api';
import type { PosImportResult, DiscountOptions } from '@/types/api';
import { EntityDrawer } from '@/components/menu-management/EntityDrawer';
import { ColumnSelector } from '@/components/menu-management/ColumnSelector';
import { EntityFormModal } from '@/components/menu-management/EntityFormModal';
import { DeleteConfirmModal } from '@/components/menu-management/DeleteConfirmModal';
import { TaxForm } from '@/components/menu-management/forms/TaxForm';
import { LanguageForm } from '@/components/menu-management/forms/LanguageForm';
import { MenuCategoryForm } from '@/components/menu-management/forms/MenuCategoryForm';
import { CookingInstructionForm } from '@/components/menu-management/forms/CookingInstructionForm';
import { MenuGroupForm } from '@/components/menu-management/forms/MenuGroupForm';
import { MenuItemForm } from '@/components/menu-management/forms/MenuItemForm';
import { CustomPaymentForm } from '@/components/menu-management/forms/CustomPaymentForm';
import { KitchenDisplayOrderForm } from '@/components/menu-management/forms/KitchenDisplayOrderForm';
import { AccountSalesAreaForm } from '@/components/menu-management/forms/AccountSalesAreaForm';
import { AskNextReasonForm } from '@/components/menu-management/forms/AskNextReasonForm';
import { DiscountForm } from '@/components/menu-management/forms/DiscountForm';
import { OrderTypeForm } from '@/components/menu-management/forms/OrderTypeForm';
import { PrinterGroupForm } from '@/components/menu-management/forms/PrinterGroupForm';
import { ShiftForm } from '@/components/menu-management/forms/ShiftForm';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { useEntityData, useEntityMutations } from '@/hooks/useEntityData';
import { useRestaurant } from '@/hooks/useRestaurant';
import { useRestaurantId } from '@/hooks/useRestaurantId';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { entityColumnsConfig } from '@/config/entityColumns';
import type { EntityType } from '@/types/api';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import { DraftModeBar } from '@/components/common/DraftModeBar';
import { PARENT_FILTERS, deriveParentOptions, applyParentFilter } from './menuParentFilter';

// Server-computed/joined fields that ride along in a fetched row but must never be
// echoed back on update — sending them back is at best ignored, at worst a stale
// last-write-wins overwrite of a field the form never displayed (H2 fix).
const SERVER_COMPUTED_KEYS = new Set([
  'uuid',
  'syncedAt',
  'syncStatus',
  'lastChangeDate',
  'createdDate',
  'modifiedDate',
  'groupName',
  'categoryName',
  'printerGroupName',
]);

function sanitizeUpdatePayload(data: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(data).filter(
      ([key]) => !SERVER_COMPUTED_KEYS.has(key) && !/^pos[A-Z]/.test(key)
    )
  );
}

export default function MenuManagementPage() {
  const restaurantId = useRestaurantId();

  const canCreate = useHasPermission(PERM.MENU_CREATE);
  const canUpdate = useHasPermission(PERM.MENU_UPDATE);
  const canDelete = useHasPermission(PERM.MENU_DELETE);

  const [selectedEntity, setSelectedEntity] = useState<EntityType>('menuCategories');
  const [searchQuery, setSearchQuery] = useState('');
  // '' = no parent filter. Only meaningful on tabs present in PARENT_FILTERS.
  const [parentFilter, setParentFilter] = useState('');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Record<string, unknown> | null>(null);
  const [deletingItem, setDeletingItem] = useState<Record<string, unknown> | null>(null);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [discountOptions, setDiscountOptions] = useState<DiscountOptions>();
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (!notification) return;
    const timer = setTimeout(() => setNotification(null), 6000);
    return () => clearTimeout(timer);
  }, [notification]);

  const queryClient = useQueryClient();
  const { data: restaurant } = useRestaurant(restaurantId ?? 0);
  const { data, isLoading, isError, isStuck, refetch } = useEntityData(selectedEntity, restaurantId ?? 0);
  const { createMutation, updateMutation, deleteMutation, hasCrud } = useEntityMutations(selectedEntity, restaurantId ?? 0);

  // Import all data from POS
  const importMutation = useMutation({
    mutationFn: () => apiClient.importAllFromPos(restaurantId ?? 0),
    onSuccess: (result: PosImportResult) => {
      // Invalidate all entity queries so data refreshes
      queryClient.invalidateQueries({ queryKey: ['entity-data'] });
      queryClient.invalidateQueries({ queryKey: ['managed-categories'] });
      queryClient.invalidateQueries({ queryKey: ['managed-groups'] });
      queryClient.invalidateQueries({ queryKey: ['menu'] });
      const details = Object.entries(result.entities)
        .filter(([, v]) => v.created > 0)
        .map(([k, v]) => `${k}: ${v.created}`)
        .join(', ');
      setNotification({
        type: 'success',
        message: `Sync complete: ${result.totalCreated} created, ${result.totalSkipped} skipped. ${details || 'No new data to import.'}`,
      });
    },
    onError: (error: Error) => {
      setNotification({ type: 'error', message: `Sync failed: ${error.message}` });
    },
  });

  const {
    selectedKeys,
    toggleColumn,
    resetToDefaults,
    allColumns,
    maxColumns,
    isAtMax,
  } = useColumnPreferences(selectedEntity, restaurantId);

  // Fetch discount options when viewing discounts
  useEffect(() => {
    if (selectedEntity === 'discounts' && restaurantId) {
      apiClient.getDiscountOptions(restaurantId)
        .then(setDiscountOptions)
        .catch(() => setDiscountOptions(undefined));
    }
  }, [selectedEntity, restaurantId]);

  const handleEntityChange = (entity: EntityType) => {
    setSelectedEntity(entity);
    setSearchQuery('');
    setParentFilter('');
  };

  const handleEdit = (item: Record<string, unknown>) => {
    setEditingItem(item);
    setFormData({ ...item });
    setIsFormModalOpen(true);
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({});
    setIsFormModalOpen(true);
  };

  const handleDelete = (item: Record<string, unknown>) => {
    setDeletingItem(item);
    setIsDeleteModalOpen(true);
  };

  const handleSave = () => {
    if (editingItem) {
      updateMutation.mutate(
        { id: Number(editingItem.id), data: sanitizeUpdatePayload(formData) },
        {
          onSuccess: () => { setIsFormModalOpen(false); setEditingItem(null); },
          onError: (error: Error) => { setNotification({ type: 'error', message: `Save failed: ${error.message}` }); }
        }
      );
    } else {
      createMutation.mutate(formData, {
        onSuccess: () => { setIsFormModalOpen(false); },
        onError: (error: Error) => { setNotification({ type: 'error', message: `Create failed: ${error.message}` }); }
      });
    }
  };

  const handleConfirmDelete = () => {
    if (deletingItem) {
      deleteMutation.mutate(Number(deletingItem.id), {
        onSuccess: () => { setIsDeleteModalOpen(false); setDeletingItem(null); },
        onError: (error: Error) => { setNotification({ type: 'error', message: `Delete failed: ${error.message}` }); }
      });
    }
  };

  const renderForm = () => {
    switch (selectedEntity) {
      case 'taxes':
        return <TaxForm formData={formData} onChange={setFormData} />;
      case 'languages':
        return <LanguageForm formData={formData} onChange={setFormData} />;
      case 'menuCategories':
        return <MenuCategoryForm formData={formData} onChange={setFormData} restaurantId={restaurantId ?? 0} />;
      case 'cookingInstructions':
        return <CookingInstructionForm formData={formData} onChange={setFormData} />;
      case 'menuGroups':
        return <MenuGroupForm formData={formData} onChange={setFormData} restaurantId={restaurantId ?? 0} />;
      case 'menuItems':
        return <MenuItemForm formData={formData} onChange={setFormData} restaurantId={restaurantId ?? 0} />;
      case 'customPayments':
        return <CustomPaymentForm formData={formData} onChange={setFormData} />;
      case 'kitchenDisplayOrders':
        return <KitchenDisplayOrderForm formData={formData} onChange={setFormData} />;
      case 'accountSalesAreas':
        return <AccountSalesAreaForm formData={formData} onChange={setFormData} />;
      case 'askNextReasons':
        return <AskNextReasonForm formData={formData} onChange={setFormData} restaurantId={restaurantId ?? 0} />;
      case 'discounts':
        return <DiscountForm formData={formData} onChange={setFormData} options={discountOptions} />;
      case 'orderTypes':
        return <OrderTypeForm formData={formData} onChange={setFormData} />;
      case 'printerGroups':
        return <PrinterGroupForm formData={formData} onChange={setFormData} />;
      case 'shifts':
        return <ShiftForm formData={formData} onChange={setFormData} />;
      default:
        return <p className="text-sm text-gray-500">Edit not yet available for this entity type.</p>;
    }
  };

  const parentFilterConfig = PARENT_FILTERS[selectedEntity];

  // Options come from `data`, not `filteredData`, so the dropdown doesn't
  // shrink as the user types in the search box.
  const parentOptions = useMemo(
    () =>
      parentFilterConfig
        ? deriveParentOptions((data ?? []) as Record<string, unknown>[], parentFilterConfig)
        : [],
    [data, parentFilterConfig]
  );

  // A selection can outlive its data (Sync Menu, a delete, a parent reassigned).
  // Dropping it avoids an empty table with no visible cause. The isLoading guard
  // keeps a first load's empty `data` from clearing a valid selection.
  useEffect(() => {
    if (!parentFilter || isLoading) return;
    if (!parentOptions.some((option) => option.value === parentFilter)) {
      setParentFilter('');
    }
  }, [parentFilter, parentOptions, isLoading]);

  // Build DataTable columns from selected keys + entityColumnsConfig + actions
  const tableColumns = useMemo<Column<Record<string, unknown>>[]>(() => {
    const config = entityColumnsConfig[selectedEntity];
    const dataCols = selectedKeys
      .map((key) => {
        const colConfig = config.all.find((c) => c.key === key);
        if (!colConfig) return null;
        const column: Column<Record<string, unknown>> = {
          key: colConfig.key,
          header: colConfig.header,
          sortable: colConfig.sortable,
          render: colConfig.render
            ? (item: Record<string, unknown>) => colConfig.render!(item)
            : undefined,
        };
        return column;
      })
      .filter((c): c is Column<Record<string, unknown>> => c !== null);

    // Only add actions column when entity supports CRUD and the user can act on it
    if (!hasCrud || (!canUpdate && !canDelete)) return dataCols;

    const actionsCol: Column<Record<string, unknown>> = {
      key: '_actions',
      header: '',
      sortable: false,
      width: '80px',
      align: 'right' as const,
      render: (item: Record<string, unknown>) => (
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {canUpdate && (
            <button
              type="button"
              onClick={() => handleEdit(item)}
              className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
              title="Edit"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={() => handleDelete(item)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    };

    return [...dataCols, actionsCol];
  }, [selectedEntity, selectedKeys, hasCrud, canUpdate, canDelete]);

  // Parent scope first, then the search query (case-insensitive substring match
  // against visible column values) — the two compose as AND.
  const filteredData = useMemo(() => {
    const scoped = applyParentFilter(
      (data ?? []) as Record<string, unknown>[],
      parentFilterConfig,
      parentFilter
    );
    if (!searchQuery.trim()) return scoped;

    const query = searchQuery.toLowerCase();
    return scoped.filter((item) =>
      selectedKeys.some((key) => {
        const value = String(item[key] ?? '');
        return value.toLowerCase().includes(query);
      })
    );
  }, [data, searchQuery, selectedKeys, parentFilterConfig, parentFilter]);

  if (restaurantId === null) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <div className="text-gray-900 font-medium">Invalid restaurant ID</div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] overflow-hidden rounded-lg border border-gray-200 bg-white">
      <EntityDrawer selectedEntity={selectedEntity} onSelect={handleEntityChange} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Toolbar */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
          {restaurant?.name && (
            <span className="text-sm font-semibold text-blue-800 whitespace-nowrap mr-1">
              {restaurant.name}
            </span>
          )}
          {/* Search Input */}
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Parent scope — rendered only on tabs whose rows carry a parent
              reference (menu groups -> category, menu items -> menu group). */}
          {parentFilterConfig && (
            <select
              aria-label={parentFilterConfig.ariaLabel}
              value={parentFilter}
              onChange={(e) => setParentFilter(e.target.value)}
              className="max-w-[14rem] py-2 pl-3 pr-8 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">{parentFilterConfig.allLabel}</option>
              {parentOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} ({option.count})
                </option>
              ))}
            </select>
          )}

          {/* Item Count */}
          <span className="text-sm text-gray-500 whitespace-nowrap">
            {filteredData.length} {filteredData.length === 1 ? 'item' : 'items'}
          </span>

          {isStuck && (
            <span
              className="text-sm text-amber-600 whitespace-nowrap"
              title="One or more rows have been pending sync for over 2 minutes; automatic refresh has stopped."
            >
              Sync stuck
            </span>
          )}

          {/* Column Selector */}
          <ColumnSelector
            allColumns={allColumns}
            selectedKeys={selectedKeys}
            onToggle={toggleColumn}
            onReset={resetToDefaults}
            isAtMax={isAtMax}
            maxColumns={maxColumns}
          />

          {/* Sync Menu Button */}
          {canCreate && (
            <button
              type="button"
              onClick={() => importMutation.mutate()}
              disabled={importMutation.isPending}
              className="btn btn-primary flex items-center gap-1"
            >
              <RefreshCw className={`w-4 h-4 ${importMutation.isPending ? 'animate-spin' : ''}`} />
              {importMutation.isPending ? 'Syncing...' : 'Sync Menu'}
            </button>
          )}

          {/* Add Button */}
          {hasCrud && canCreate && (
            <button type="button" onClick={handleCreate} className="btn btn-outline flex items-center gap-1">
              <Plus className="w-4 h-4" />
              Add
            </button>
          )}
        </div>

        {/* Draft mode — covers every entity type on this page, so the toggle sits
            above the entity list rather than inside any one tab. */}
        {restaurantId !== null && (
          <div className="px-4 py-3 border-b border-gray-200">
            <DraftModeBar
              restaurantId={restaurantId}
              area="MENU"
              permission={PERM.MENU_UPDATE}
            />
          </div>
        )}

        {/* Notification */}
        {notification && (
          <div
            className={`flex items-center justify-between gap-3 px-4 py-2 text-sm border-b ${
              notification.type === 'error'
                ? 'bg-red-50 text-red-700 border-red-100'
                : 'bg-green-50 text-green-700 border-green-100'
            }`}
          >
            <span className="whitespace-pre-line">{notification.message}</span>
            <button
              type="button"
              onClick={() => setNotification(null)}
              className="text-current opacity-70 hover:opacity-100"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {isError ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
              <AlertTriangle className="h-8 w-8 text-red-500" />
              <div className="text-gray-900 font-medium">Failed to load {entityColumnsConfig[selectedEntity].label.toLowerCase()}</div>
              <button type="button" onClick={() => refetch()} className="btn btn-outline h-10">
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </button>
            </div>
          ) : (
            <DataTable
              data={filteredData}
              columns={tableColumns}
              keyExtractor={(item) => String(item.id)}
              pageSize={20}
              loading={isLoading}
              stickyHeader
              onRowClick={canUpdate ? (item) => handleEdit(item) : undefined}
            />
          )}
        </div>
      </div>

      {/* Edit/Create Modal */}
      <EntityFormModal
        isOpen={isFormModalOpen}
        onClose={() => { setIsFormModalOpen(false); setEditingItem(null); }}
        title={editingItem ? `Edit ${entityColumnsConfig[selectedEntity].singularLabel}` : `New ${entityColumnsConfig[selectedEntity].singularLabel}`}
        onSave={handleSave}
        isSaving={createMutation.isPending || updateMutation.isPending}
      >
        {renderForm()}
      </EntityFormModal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeletingItem(null); }}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteMutation.isPending}
        entityName={entityColumnsConfig[selectedEntity].singularLabel}
        itemName={String(deletingItem?.name || deletingItem?.description || '')}
      />
    </div>
  );
}
